import React, { useState } from "react";
import { toast } from "sonner";
import { MessageSquareWarning, Check, Send, Loader2, Ban } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { api } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { qtyWithUnit } from "../lib/units";
import { Button } from "./ui/button";

export default function RevisionBanner({ revision, items, quoteId, onApply, onSaveBeforeResend, onResent, onRejected }) {
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [note, setNote] = useState("");
  const reject = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${quoteId}/reject-revision`, { note: note.trim() });
      toast.success(data.email_sent ? "Proposta respinta: il cliente è stato avvisato via email" : "Proposta respinta: il preventivo originale è di nuovo firmabile");
      setRejectOpen(false);
      onRejected();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
    setBusy(false);
  };
  const changes = Object.entries(revision.quantities || {});
  const current = Object.fromEntries((items || []).map((it) => [it.id, Number(it.quantity)]));
  const applied = changes.length > 0 && changes.every(([id, c]) => current[id] === c.to);

  const resend = async () => {
    setBusy(true);
    const ok = await onSaveBeforeResend();
    if (ok) {
      try {
        const { data } = await api.post(`/quotes/${quoteId}/resend-revision`);
        toast.success(data.email_sent ? "Preventivo aggiornato e rinviato al cliente via email" : "Preventivo aggiornato: stato tornato a Inviato");
        onResent();
      } catch (e) { toast.error(e?.response?.data?.detail || "Rinvio non riuscito"); }
    }
    setBusy(false);
  };

  return (
    <div data-testid="revision-banner" className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-5">
      <div className="flex items-start gap-3">
        <MessageSquareWarning style={{ width: 20, height: 20 }} className="text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-slate-900">Modifica richiesta dal cliente{revision.client_name ? ` (${revision.client_name})` : ""}</p>
          <p className="text-xs text-slate-500">{fmtDateTime(revision.created_at)}</p>
          {changes.length > 0 && (
            <ul className="mt-3 space-y-1.5" data-testid="revision-changes">
              {changes.map(([id, c]) => (
                <li key={id} className="text-sm flex items-center gap-2 flex-wrap" data-testid={`revision-change-${id}`}>
                  <span className="font-medium text-slate-900">{c.title || "Voce"}</span>
                  <span className="font-mono text-slate-500 line-through">{qtyWithUnit(c.from, c.unit)}</span>
                  <span className="text-slate-400">→</span>
                  <span className="font-mono font-bold text-amber-700">{qtyWithUnit(c.to, c.unit)}</span>
                </li>
              ))}
            </ul>
          )}
          {revision.message && <p className="mt-3 text-sm text-slate-700 bg-card/70 rounded-lg px-3 py-2 border border-amber-200" data-testid="revision-message">"{revision.message}"</p>}
          <p className="text-xs text-slate-500 mt-3">Applica le quantità richieste (o modificale), adegua i prezzi se serve, poi rinvia il preventivo aggiornato al cliente.</p>
          <div className="flex gap-2 mt-3 flex-wrap">
            {changes.length > 0 && (
              <Button size="sm" variant="outline" data-testid="revision-apply" disabled={applied}
                onClick={() => { onApply(Object.fromEntries(changes.map(([id, c]) => [id, c.to]))); toast.success("Quantità richieste applicate alle voci"); }}>
                <Check style={{ width: 14, height: 14 }} className="mr-1.5" /> {applied ? "Quantità applicate" : "Applica quantità richieste"}
              </Button>
            )}
            <Button size="sm" data-testid="revision-resend" onClick={resend} disabled={busy} className="font-semibold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send style={{ width: 14, height: 14 }} className="mr-1.5" />} Salva e rinvia al cliente
            </Button>
            <Button size="sm" variant="outline" data-testid="revision-reject" onClick={() => { setNote(""); setRejectOpen(true); }} disabled={busy}
              className="border-red-300 text-red-700 hover:bg-red-50 font-semibold">
              <Ban style={{ width: 14, height: 14 }} className="mr-1.5" /> Quantità non disponibili
            </Button>
          </div>
        </div>
      </div>
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="revision-reject-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Quantità non disponibili</DialogTitle>
            <DialogDescription>La proposta del cliente verrà respinta: le quantità tornano a quelle originali e il preventivo sarà di nuovo firmabile. Il cliente vedrà "Spiacenti, quantità non disponibile" e riceverà un'email.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="revision-reject-note" rows={3} maxLength={500} placeholder="Nota per il cliente (facoltativa), es. max 5 disponibili" value={note} onChange={(e) => setNote(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Annulla</Button>
            <Button data-testid="revision-reject-confirm" onClick={reject} disabled={busy} className="bg-red-600 hover:bg-red-700 font-semibold">
              {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Respingi proposta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
